/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Templates", description = "Manage the email templates that Lifecycle Studio uses as structural starting points when generating messages — import existing HTML and save finished messages as new templates.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Templates",
        description: "Manage the email templates that Lifecycle Studio uses as structural starting points when generating messages — import existing HTML and save finished messages as new templates.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Import templates",
          "depth": 0,
          "id": "import-templates"
        }, {
          "value": "Import from ESP",
          "depth": 0,
          "id": "import-from-esp"
        }, {
          "value": "Import from Figma",
          "depth": 0,
          "id": "import-from-figma"
        }, {
          "value": "Connect Figma",
          "depth": 1,
          "id": "connect-figma"
        }, {
          "value": "Import a frame",
          "depth": 1,
          "id": "import-a-frame"
        }, {
          "value": "Recommended Figma design practices",
          "depth": 1,
          "id": "recommended-figma-design-practices"
        }, {
          "value": "Review the best practice analysis",
          "depth": 0,
          "id": "review-the-best-practice-analysis"
        }, {
          "value": "Control which templates the agent uses",
          "depth": 0,
          "id": "control-which-templates-the-agent-uses"
        }, {
          "value": "Save a message as a template",
          "depth": 0,
          "id": "save-a-message-as-a-template"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      ol: "ol",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/lifecycle-studio/overview#get-started",
                    children: "Lifecycle Studio setup complete"
                  })
                })
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Templates are the layouts the agent builds messages on top of. Import your existing email HTML and save finished messages back to the library for reuse."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/templates/ls-templates-overview.png",
          alt: "Templates grid view"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Templates provide the layout and structural foundation for generated messages. When the agent creates an email, whether from a ", _jsx(_components.a, {
          href: "/lifecycle-studio/messages",
          children: "message"
        }), " or a ", _jsx(_components.a, {
          href: "/lifecycle-studio/campaigns",
          children: "campaign"
        }), ", it builds the content on top of a template from this library."]
      }), "\n", _jsxs(_components.p, {
        children: ["Templates are different from brand guidelines. Brand guidelines (configured in ", _jsx(_components.a, {
          href: "/context-hub/brands",
          children: "Context Hub"
        }), ") control tone, voice, and visual rules. Templates control the structural layout: header placement, section order, CTA positioning, and responsive behavior."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "import-templates",
        children: "Import templates"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "https://app.hightouch.com/lifecycle-studio/templates?from=docs",
            children: "Lifecycle Studio → Templates"
          })
        }), " and select ", _jsx(_components.strong, {
          children: "Import template"
        }), ". You can import from three sources:"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/templates/ls-templates-import-menu.png",
          alt: "Templates page with the Import template menu open"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Import HTML or .eml"
          }), " — Paste raw HTML or drag and drop an HTML or .eml file. Give the template a name and description, assign it to one or more brand profiles, and optionally enable ", _jsx(_components.strong, {
            children: "Beautify on paste"
          }), " to auto-format the HTML."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Import from ESP"
          }), " — Pull existing templates directly from a connected email service provider. This option requires a provider connection configured in ", _jsx(_components.a, {
            href: "/context-hub/connections",
            children: "Context Hub"
          }), ". See ", _jsx(_components.a, {
            href: "#import-from-esp",
            children: "Import from ESP"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Import from Figma"
          }), " — Import a Figma frame as an editable draft you can promote to a template or component. This requires a Figma connection in Context Hub. See ", _jsx(_components.a, {
            href: "#import-from-figma",
            children: "Import from Figma"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/messages/ls-messages-email-builder-import.png",
          alt: "Import email dialog with name, description, brand profiles, and HTML content"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Import a range of template styles that cover your main campaign types: promotional, transactional, editorial, and notification. The agent selects templates based on the campaign context and message content, so a varied library produces more varied output."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "import-from-esp",
        children: "Import from ESP"
      }), "\n", _jsxs(_components.p, {
        children: ["Import existing email templates from a connected email service provider (ESP) into your library. This requires an ESP connection. Set one up in ", _jsx(_components.a, {
          href: "/context-hub/connections",
          children: "Context Hub"
        }), " first."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://app.hightouch.com/lifecycle-studio/templates?from=docs",
              children: "Lifecycle Studio → Templates"
            })
          }), " and select ", _jsx(_components.strong, {
            children: "Import template → Import from ESP"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose an email provider. If you don't have one connected, select ", _jsx(_components.strong, {
            children: "Connect your ESP"
          }), " to add a destination first."]
        }), "\n", _jsxs(_components.li, {
          children: ["Find the connected destination and select ", _jsx(_components.strong, {
            children: "View Templates"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Browse Email Templates"
          }), " dialog, search for the templates you want and select one or more. Select a row to preview its subject line, preheader, tags, and HTML."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally assign the templates to one or more brand profiles, then select ", _jsx(_components.strong, {
            children: "Import"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "import-from-figma",
        children: "Import from Figma"
      }), "\n", _jsx(_components.p, {
        children: "Design teams often build email layouts in Figma before they exist as HTML. The Figma import recreates a frame as an editable email draft. It rebuilds the layout and separates the design into editable layers for images, backgrounds, text, and copy, so you can adjust the result in the email editor instead of rebuilding it by hand. You then promote the draft to a template or a reusable component."
      }), "\n", _jsxs(_components.p, {
        children: ["Imported designs land in the ", _jsx(_components.strong, {
          children: "Figma drafts"
        }), " tab on the Templates page. Preview a draft, refine it in the editor if you need to, then promote it into your library."]
      }), "\n", _jsx(_components.h3, {
        id: "connect-figma",
        children: "Connect Figma"
      }), "\n", _jsxs(_components.p, {
        children: ["Before your first import, ", _jsx(_components.a, {
          href: "/context-hub/connections#connect-figma-designs",
          children: "connect Figma Designs"
        }), " in Context Hub with a Figma personal access token. The token needs the ", _jsx(_components.code, {
          children: "current_user:read"
        }), " and ", _jsx(_components.code, {
          children: "file_content:read"
        }), " scopes, and its owner must be able to open the files you import."]
      }), "\n", _jsx(_components.h3, {
        id: "import-a-frame",
        children: "Import a frame"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "https://app.hightouch.com/lifecycle-studio/templates?from=docs",
              children: "Lifecycle Studio → Templates"
            })
          }), " and select ", _jsx(_components.strong, {
            children: "Import template → Import from Figma"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Template name"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste the ", _jsx(_components.strong, {
            children: "Figma frame URL"
          }), " for the design you want to import."]
        }), "\n", _jsxs(_components.li, {
          children: ["To import a two-frame responsive design, also paste a ", _jsx(_components.strong, {
            children: "Mobile frame URL"
          }), ". This is optional."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Start import"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/templates/ls-templates-figma-import.png",
          alt: "Import from Figma drawer with fields for template name and the Figma and mobile frame URLs"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The import runs in the background and appears in the ", _jsx(_components.strong, {
          children: "Figma drafts"
        }), " tab. Open a draft to preview it, and select ", _jsx(_components.strong, {
          children: "Edit"
        }), " to refine it in the email editor, where you can adjust any layer, copy, or color, or iterate with the agent chat. When the draft is ready, select ", _jsx(_components.strong, {
          children: "Promote → Promote to template"
        }), " or ", _jsx(_components.strong, {
          children: "Promote to component"
        }), " to move it into your library."]
      }), "\n", _jsx(_components.h3, {
        id: "recommended-figma-design-practices",
        children: "Recommended Figma design practices"
      }), "\n", _jsx(_components.p, {
        children: "Import quality depends on how the source file is built. For the best results, structure your Figma file so the agent can interpret it cleanly:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use auto layout"
          }), " for structured sections."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name layers clearly"
          }), " so the agent can identify each element."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Apply tokens"
          }), " for text and color."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Files that are missing these elements still import, but you're more likely to need manual edits afterward to match the original design."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "review-the-best-practice-analysis",
        children: "Review the best practice analysis"
      }), "\n", _jsx(_components.p, {
        children: "When you import or edit a template, Lifecycle Studio checks its HTML against a set of email best practices and fixes issues that affect how the email renders across clients. The checks cover compatibility (such as Outlook), mobile responsiveness, CSS patterns, dark mode, accessibility, and structure."
      }), "\n", _jsxs(_components.p, {
        children: ["Open a template and select ", _jsx(_components.strong, {
          children: "View best practice analysis"
        }), " to see a before-and-after comparison of what the agent changed and why."]
      }), "\n", _jsxs(_components.p, {
        children: ["You control which rules apply in ", _jsx(_components.a, {
          href: "/context-hub/overview",
          children: "Context Hub"
        }), " under Best Practices. Turn off any rules that don't fit your templates."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "control-which-templates-the-agent-uses",
        children: "Control which templates the agent uses"
      }), "\n", _jsxs(_components.p, {
        children: ["Each template has an ", _jsx(_components.strong, {
          children: "Enabled for Agents"
        }), " toggle. Turn it on to let the agent choose that template when generating messages, or off to keep a template in your library without using it for generation. Use this to curate the layouts the agent draws from, so one-off or seasonal templates stay out of everyday generation."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "save-a-message-as-a-template",
        children: "Save a message as a template"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/templates/ls-save-message-template.png",
          alt: "Export menu with Save to template option"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can save any finished message back to the template library from the ", _jsx(_components.a, {
          href: "/lifecycle-studio/messages#iterate-with-the-agent-chat",
          children: "message editor"
        }), ". This is useful when you want to reuse a generated layout in future messages."]
      }), "\n", _jsx(_components.p, {
        children: "To save a message as a template:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Open the message in the editor."
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Export"
          }), " dropdown and select ", _jsx(_components.strong, {
            children: "Save to template"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Enter a name for the new template."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/messages",
            children: "Messages"
          }), " — Create and edit lifecycle messages"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/campaigns",
            children: "Campaigns"
          }), " — Plan and send end-to-end lifecycle campaigns"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/images",
            children: "Images"
          }), " — Manage the image library the agent draws from"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
